import { getBookIdWithPrefix} from "../utils/content/content";
import { localStorageSafe, setLocalStorageItem } from "../utils/storage/storage";
import { arraymove } from "../utils/array";
import {CONTENT_CLASSES, langRegex} from "../utils/content/content.constants";
import {monthNamesInitial} from "../utils/search/search.constants";
import { isBeforeDateISO } from "@/shared/utils/date";

export const withEtagSaving = (callback, eTagCacheName) => (response) => {
  const responseETag = response.headers.etag;
  const storedETag = localStorageSafe.getItem(eTagCacheName);
  if (responseETag && responseETag !== storedETag) {
    setLocalStorageItem(eTagCacheName, responseETag);
  }
  let value = null;
  if (response.data) {
    value = response.data;
    if (callback) {
      value = callback(response);
    }
  }
  return { status: response.status, value };
};

export const getOrderNumberOfMonth = (month) => {
  const matched = monthNamesInitial.findIndex((item) => item.includes(month.toLowerCase()));
  return matched !== -1 ? matched + 1 : "1";
};

export const getBookIdByCode = (periodicals = [], code, bookId) => {
  const cloned = [...periodicals] || [];
  const lowerCode = code.toLowerCase();

  // first of all we are looking up in the same book
  const filteredByBookId = cloned.filter(
    (item) => item.book_id === bookId && item.code.toLowerCase() === lowerCode,
  );

  if (filteredByBookId.length) {
    return filteredByBookId[0].book_id;
  }

  const filteredByCode = cloned.filter((item) => item.code.toLowerCase() === lowerCode);

  if (filteredByCode.length) {
    return filteredByCode[0].book_id;
  }

  return null;
};

export const removeLangFromFolders = (folders = []) => {
  return folders.filter((item) => !langRegex.test(item) && item !== "all");
};

export const joinHistoryData = (
  readData = [],
  listenData = [],
  mlData = [],
  libraryData = [],
  allHistory = [],
  isUpdate,
) => {
  const allHistoryNew = {};
  const allHistoryOldMap = {};

  const getAllHistoryOldItem = (id) => {
    let item = allHistoryOldMap[id];

    if (!item && item !== null) {
      item = allHistory?.find((i) => i.id === id) || null;
      allHistoryOldMap[id] = item;
    }

    return item;
  };

  let itemHolder;

  if (readData?.items) {
    readData.items.forEach((item) => {
      if (item.id) {
        if (!allHistoryNew[item.id]) {
          allHistoryNew[item.id] = getAllHistoryOldItem(item.id) || {};
        }

        itemHolder = allHistoryNew[item.id];

        if (isBeforeDateISO(itemHolder.luRead, item.lu)) {
          itemHolder.luRead = item.lu;
          itemHolder.readParaId = item.para_id;
          itemHolder.readRefCodeShort = item.refcode_short;
          itemHolder.readRefCodeLong = item.refcode_long;
          itemHolder.readChapterId = item.chapter_id;
          itemHolder.readPosition = item.position;

          itemHolder.lang = item.lang;
          itemHolder.id = getBookIdWithPrefix(item.id);
        }
      }
    });
  }

  if (listenData?.items) {
    listenData.items.forEach((item) => {
      if (item.id) {
        if (!allHistoryNew[item.id]) {
          allHistoryNew[item.id] = getAllHistoryOldItem(item.id) || {};
        }

        itemHolder = allHistoryNew[item.id];

        if (isBeforeDateISO(itemHolder.luListen, item.lu)) {
          itemHolder.luListen = item.lu;
          itemHolder.listenPosition = item.position;
          itemHolder.listenChapterId = item.book + "." + item.chapter;

          itemHolder.lang = item.lang;
          itemHolder.id = getBookIdWithPrefix(item.book);
        }
      }
    });
  }

  if (mlData?.items) {
    mlData.items.forEach((item) => {
      if (item.id) {
        if (!allHistoryNew[item.id]) {
          allHistoryNew[item.id] = getAllHistoryOldItem(item.id) || {};
        }

        itemHolder = allHistoryNew[item.id];

        if (isBeforeDateISO(itemHolder.mlLu, item.lu)) {
          itemHolder.mlParaId = item.para_id;
          itemHolder.mlChapterId = item.chapter_id;
          itemHolder.mlPosition = item.position;
          itemHolder.mlLu = item.lu;
          itemHolder.mlRefCodeShort = item.refcode_short;
          itemHolder.mlRefCodeLong = item.refcode_long;
          itemHolder.id = getBookIdWithPrefix(item.book);
        }
      }
    });
  }

  if (libraryData?.items) {
    libraryData.items.forEach((item) => {
      if (item.id) {
        if (!allHistoryNew[item.id]) {
          allHistoryNew[item.id] = getAllHistoryOldItem(item.id) || {};
        }

        itemHolder = allHistoryNew[item.id];

        if (isBeforeDateISO(itemHolder.luLibrary, item.lu)) {
          itemHolder.luLibrary = item.lu;
          itemHolder.id = getBookIdWithPrefix(item.book);
        }

        itemHolder.inLibrary = true;
      }
    });
  }

  if (mlData?.deleted) {
    mlData.deleted.forEach((item) => {
      if (item.id) {
        if (allHistoryNew[item.id]) {
          allHistoryNew[item.id].inLibrary = false;
        }
      }
    });
  }

  const allHistoryNewArr = Object.values(allHistoryNew);

  if (isUpdate) {
    // if is update, join old and new items.
    const allHistoryUpdated = [...allHistory];

    let allHistoryNewItem;
    for (let i = 0; i < allHistoryNewArr.length; i++) {
      allHistoryNewItem = allHistoryNewArr[i];

      const index = allHistory.findIndex((item) => item.id === allHistoryNewItem.id);

      if (index === -1) {
        allHistoryUpdated.unshift(allHistoryNewItem);
      } else {
        allHistoryUpdated[index] = allHistoryNewItem;
      }
    }

    return allHistoryUpdated;
  }

  return allHistoryNewArr;
};

export const makeHistoryUrl = (historyType, withDelete, start, end) => {
  const params = [`deleted=${withDelete}`];
  if (start) {
    params.push(`start=${start}`);
  }
  if (end) {
    params.push(`end=${end}`);
  }
  return `settings/${historyType}/sync?${params.join("&")}`;
};

export const makeAggregateUrl = (oUrls) => {
  let elements = Object.entries(oUrls).map(([key, value]) => {
    let urlValue = value;
    if (urlValue.indexOf("/") !== 0) {
      urlValue = "/" + urlValue;
    }
    return key + "=" + encodeURIComponent(urlValue);
  });

  return "/aggregate/?" + elements.join("&");
};

export const mapReadingHistoryForServer = (paraId, position, dateISO) => ({
  para_id: paraId,
  position: position,
  lu: dateISO,
});

export const mapListenHistoryForServer = (paraId, position, dateISO) => {
  const [bookId, chapter] = paraId.split(".");
  return {
    book: bookId,
    chapter: chapter,
    lu: dateISO,
    position: position,
  };
};

export const parseResponseContentLanguages = (langList, lang) => {
  let languages = langList.map((item) => ({
    ...item,
    label: item.name,
    id: item.code,
    className: CONTENT_CLASSES.LANGUAGE,
  }));

  if (lang) {
    const index = languages.findIndex((item) => item.id === lang);

    if (index > -1 && index !== 0) {
      arraymove(languages, index, 0);
    }
  }

  return languages;
};

export const parseErrorDefault = (error) => error.data;
export const parseResponseDefault = (response) => response.data;
